import { AgentTurnType, ApprovalStepStatus } from 'ui-product-approvals-common/types/ApprovalData';
import { ApprovalRules } from 'ui-product-approvals-common/types/ApprovalRules';
import { ApprovalStatus } from 'ui-product-approvals-common/types/ApprovalStatus';
import { serializeActor } from 'ui-product-approvals-common/utils/actorKey';
import { ACTOR } from '../constants/actors';
export const groupRowsByOrder = approvalSteps => {
  const byOrder = new Map();
  const withoutOrder = [];
  approvalSteps.forEach(step => {
    var _byOrder$get;
    if (step.order === undefined) {
      withoutOrder.push(step.approver);
      return;
    }
    const group = (_byOrder$get = byOrder.get(step.order)) !== null && _byOrder$get !== void 0 ? _byOrder$get : [];
    group.push(step.approver);
    byOrder.set(step.order, group);
  });
  const orderedGroups = [...byOrder.entries()].sort(([orderA], [orderB]) => orderA - orderB).map(([, group]) => group);
  return withoutOrder.length > 0 ? [withoutOrder, ...orderedGroups] : orderedGroups;
};
export const groupStepsByOrder = approvalSteps => {
  const byOrder = new Map();
  approvalSteps.forEach(step => {
    var _step$order, _byOrder$get2;
    const order = (_step$order = step.order) !== null && _step$order !== void 0 ? _step$order : 0;
    const group = (_byOrder$get2 = byOrder.get(order)) !== null && _byOrder$get2 !== void 0 ? _byOrder$get2 : [];
    group.push(step);
    byOrder.set(order, group);
  });
  return [...byOrder.entries()].sort(([orderA], [orderB]) => orderA - orderB).map(([, group]) => group);
};
export const isGroupComplete = (group, approvalRules) => {
  const isApproved = step => step.approvalStepStatus === ApprovalStepStatus.APPROVED;
  return approvalRules === ApprovalRules.ANY_CAN_APPROVE ? group.some(isApproved) : group.every(isApproved);
};

// Mirrors the legacy getStepStyle "primary" logic: the decisive approver is the
// one whose step status matches the overall outcome (rejected / changes
// requested, or approved under ANY_CAN_APPROVE). Returns a new array with the
// decisive approver(s) first, preserving the original order of the rest.
export const sortStepsDecisiveFirst = (steps, approvalStatus, approvalRules) => {
  const decisiveStatus = approvalStatus === ApprovalStatus.REJECTED ? ApprovalStepStatus.REJECTED : approvalStatus === ApprovalStatus.CHANGES_REQUESTED ? ApprovalStepStatus.CHANGES_REQUESTED : approvalStatus === ApprovalStatus.APPROVED && approvalRules === ApprovalRules.ANY_CAN_APPROVE ? ApprovalStepStatus.APPROVED : null;
  if (!decisiveStatus) {
    return steps;
  }
  return [...steps.filter(step => step.approvalStepStatus === decisiveStatus), ...steps.filter(step => step.approvalStepStatus !== decisiveStatus)];
};
export const isOrderedApproval = steps => groupStepsByOrder(steps).length > 1;
export const getStepGroupCount = steps => groupStepsByOrder(steps).length;
export const getCompletedStepGroupCount = (steps, approvalRules) => groupStepsByOrder(steps).filter(group => isGroupComplete(group, approvalRules)).length;
export const getIncompleteStepGroupCount = (steps, approvalRules) => getStepGroupCount(steps) - getCompletedStepGroupCount(steps, approvalRules);
export const shouldWarnInvalidApprover = (step, validApproverActorKeys, isFetchApproversError, isLoadingApprovers) => step.approver.actor.actorType === ACTOR.USER && !isFetchApproversError && !isLoadingApprovers && step.approvalStepStatus === ApprovalStepStatus.APPROVAL_PENDING && !validApproverActorKeys.has(serializeActor(step.approver.actor));
export const isAgentReviewingApprovalStep = step => step.approvalStepStatus === ApprovalStepStatus.APPROVAL_PENDING && step.approver.actor.actorType === ACTOR.AGENT && step.inProgressTurnType === AgentTurnType.REVIEW;
export const toApprovalStepInputs = steps => steps.filter(step => step.approvers.length > 0).flatMap((step, index) => step.approvers.map(approver => ({
  actor: approver.actor,
  order: index
})));
export const existingStepsToInputs = approvalSteps => approvalSteps.map(step => ({
  actor: step.approver.actor,
  order: step.order
}));
export const countDistinctApprovers = steps => {
  const actorKeys = new Set();
  steps.forEach(step => step.approvers.forEach(approver => actorKeys.add(serializeActor(approver.actor))));
  return actorKeys.size;
};
export const hasEmptyStep = steps => steps.some(step => step.approvers.length === 0);
export const hasHumanApprover = steps => steps.some(step => step.approvers.some(approver => approver.actor.actorType === ACTOR.USER));
export const stepHasLockedApprover = (step, lockedActorKeys) => step.approvers.some(approver => lockedActorKeys.has(serializeActor(approver.actor)));
export const isStepGroupComplete = (step, approvedActorKeys, approvalRules) => {
  if (step.approvers.length === 0) {
    return false;
  }
  const isApproved = approver => approvedActorKeys.has(serializeActor(approver.actor));
  return approvalRules === ApprovalRules.ANY_CAN_APPROVE ? step.approvers.some(isApproved) : step.approvers.every(isApproved);
};
export const reorderChangesLockedStep = (steps, fromIndex, toIndex, lockedActorKeys) => {
  const reordered = [...steps];
  const [moved] = reordered.splice(fromIndex, 1);
  if (moved === undefined) {
    return false;
  }
  reordered.splice(toIndex, 0, moved);
  return steps.some((step, index) => {
    var _reordered$index;
    return stepHasLockedApprover(step, lockedActorKeys) && ((_reordered$index = reordered[index]) === null || _reordered$index === void 0 ? void 0 : _reordered$index.id) !== step.id;
  });
};

// Maps an insertion seam (gap index in [0, n]) to the splice index for
// onReorderSteps(from, to). Returns null when the seam is adjacent to the
// dragged step (a no-op move).
export const stepReorderToIndex = (fromIndex, seam) => {
  if (seam === fromIndex || seam === fromIndex + 1) {
    return null;
  }
  return fromIndex < seam ? seam - 1 : seam;
};

// Resolves the splice index for a step drag dropped at `seam`, or null when the
// drop is a no-op / not allowed (bad item, adjacent seam, or a locked-step move).
export const resolveStepDropIndex = (item, seam, canReorder) => {
  if (!item || typeof item.index !== 'number') {
    return null;
  }
  const toIndex = stepReorderToIndex(item.index, seam);
  return toIndex !== null && canReorder(item.index, toIndex) ? toIndex : null;
};
export const approvalStepsSignature = steps => steps.filter(step => step.approvers.length > 0).map(step => step.approvers.map(approver => serializeActor(approver.actor)).sort().join(',')).join('|');